import React, { useState, useRef } from 'react';
import { TrendingUp, TrendingDown, Activity } from 'lucide-react';

// Derive WebSocket base from the REST API URL env var
const WS_BASE = (process.env.REACT_APP_API_URL || 'https://daytrader.thecreativecoefficient.com.au')
  .replace('https://', 'wss://')
  .replace('http://', 'ws://');

const STATS = [
  { label: '~3,080 tickers',    sub: 'pre-filtered universe' },
  { label: 'Under 200M float',  sub: 'micro / small-cap only' },
  { label: 'Real-time RVOL',    sub: 'vs 20-day baseline'     },
];

const Stage1Dashboard = () => {
  const [stocks, setStocks]                     = useState([]);
  const [isRunning, setIsRunning]               = useState(false);
  const [anchorDate, setAnchorDate]             = useState('');
  const [anchorTime, setAnchorTime]             = useState('09:25:00');
  const [statusMessage, setStatusMessage]       = useState('');
  const [completionMessages, setCompletionMessages] = useState([]);
  const [filters, setFilters]                   = useState('');
  const wsRef = useRef(null);

  // ── Formatters ────────────────────────────────────────────────────────────

  const formatNumber = (num, decimals = 2) =>
    num?.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  const formatVolume = (vol) => {
    if (vol >= 1_000_000) return `${(vol / 1_000_000).toFixed(2)}M`;
    if (vol >= 1_000)     return `${(vol / 1_000).toFixed(0)}K`;
    return vol;
  };

  // ── Screener ──────────────────────────────────────────────────────────────

  const handleRunScreener = () => {
    if (!anchorDate) { alert('Please select a date'); return; }

    setStocks([]);
    setCompletionMessages([]);
    setStatusMessage('');
    setFilters('');
    setIsRunning(true);

    const websocket = new WebSocket(`${WS_BASE}/ws/stage1/run`);
    wsRef.current = websocket;

    websocket.onopen = () => {
      websocket.send(JSON.stringify({
        anchor:  `${anchorDate} ${anchorTime}`,
        api_key: process.env.REACT_APP_API_KEY,
      }));
    };

    websocket.onmessage = (event) => {
      const message = JSON.parse(event.data);
      switch (message.type) {
        case 'status':   setStatusMessage(message.message); break;
        case 'filters':  setFilters(message.message); break;
        case 'ticker':
          setStocks(prev => {
            if (prev.some(s => s.ticker === message.data.ticker)) return prev;
            return [...prev, message.data];
          });
          break;
        case 'info':     setCompletionMessages(prev => [...prev, message.message]); break;
        case 'complete':
          setCompletionMessages(prev => [...prev, message.message]);
          setStatusMessage('');
          setIsRunning(false);
          break;
        case 'error':
          alert(`Error: ${message.message}`);
          setStatusMessage('');
          setIsRunning(false);
          websocket.close();
          break;
        default: break;
      }
    };

    websocket.onerror  = () => { setStatusMessage('Connection error'); setIsRunning(false); };
    websocket.onclose  = () => { setIsRunning(false); setStatusMessage(''); };
  };

  const handleReset = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.close();
    setStocks([]);
    setCompletionMessages([]);
    setStatusMessage('');
    setIsRunning(false);
    setAnchorDate('');
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="bg-ink text-text-hi">

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="crosshair-bg border-b border-haze">
        <div className="mx-auto max-w-6xl px-8 py-12">
          <span className="eyebrow block mb-4">Premarket Screener</span>
          <h1 className="text-4xl font-bold leading-[1.1] text-text-hi mb-4 sm:text-5xl">
            Find the names worth watching<br />before the bell.
          </h1>
          <div className="rule-line w-10 mb-6" />
          <p className="text-text-lo text-base leading-relaxed max-w-2xl mb-8">
            Runs the full NASDAQ low-float universe through RVOL, price-change, and
            premarket-volume filters. Results stream in live as each candidate clears the screen.
          </p>
          {/* Spec chips */}
          <div className="flex flex-wrap gap-3">
            {STATS.map(({ label, sub }) => (
              <div
                key={label}
                className="inline-flex flex-col rounded-sm border border-rule/30 bg-ink/60 px-3 py-2"
              >
                <span className="font-mono text-[11px] text-rule tracking-wider">{label}</span>
                <span className="font-mono text-[10px] text-text-lo mt-0.5">{sub}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Controls ─────────────────────────────────────────────────── */}
      <div className="mx-auto max-w-6xl px-8 py-8">
        <div className="card-surface p-6">

          <span className="eyebrow block mb-5">Run Screener</span>

          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[160px]">
              <label className="block font-mono text-[11px] tracking-widest text-text-lo mb-2">
                DATE
              </label>
              <input
                type="date"
                value={anchorDate}
                onChange={e => setAnchorDate(e.target.value)}
                disabled={isRunning}
                required
                className="w-full bg-ink border border-haze text-text-hi rounded px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-rule transition-colors disabled:opacity-50"
              />
            </div>

            <div className="flex-1 min-w-[160px]">
              <label className="block font-mono text-[11px] tracking-widest text-text-lo mb-2">
                TIME (EST)
              </label>
              <input
                type="time"
                value={anchorTime}
                onChange={e => setAnchorTime(e.target.value)}
                step="60"
                disabled={isRunning}
                required
                className="w-full bg-ink border border-haze text-text-hi rounded px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-rule transition-colors disabled:opacity-50"
              />
            </div>

            <button
              onClick={handleRunScreener}
              disabled={isRunning}
              className={`px-6 py-2.5 rounded text-sm font-semibold transition-colors flex-shrink-0 ${
                isRunning
                  ? 'bg-haze text-text-lo cursor-not-allowed'
                  : 'btn-amber'
              }`}
            >
              {isRunning ? 'Running…' : 'Run Screener'}
            </button>

            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2.5 rounded text-sm font-medium border border-haze text-text-lo hover:border-rule hover:text-text-hi transition-colors flex-shrink-0"
            >
              Clear
            </button>
          </div>

          {/* Live status */}
          {statusMessage && (
            <div className="mt-4 flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-rule animate-pulse-soft flex-shrink-0" />
              <span className="font-mono text-xs text-rule tracking-wider">{statusMessage}</span>
            </div>
          )}

          {/* Completion messages */}
          {completionMessages.length > 0 && (
            <div className="mt-3 space-y-1">
              {completionMessages.map((msg, i) => (
                <div key={i} className="font-mono text-xs text-signal">{msg}</div>
              ))}
            </div>
          )}

          {/* Stats row */}
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="border border-haze rounded p-4">
              <span className="eyebrow block mb-2">Candidates Found</span>
              <div className="font-mono text-3xl text-text-hi">{stocks.length}</div>
            </div>
            <div className="border border-haze rounded p-4">
              <span className="eyebrow block mb-2">Status</span>
              <div className={`font-mono text-lg font-bold ${
                isRunning         ? 'text-rule'    :
                stocks.length > 0 ? 'text-signal'  :
                                    'text-text-lo'
              }`}>
                {isRunning ? 'RUNNING' : stocks.length > 0 ? 'COMPLETE' : 'READY'}
              </div>
            </div>
          </div>
        </div>

        {/* ── Filters banner ─────────────────────────────────────────── */}
        {filters && (
          <div className="mt-4 flex items-start gap-3 border border-haze rounded p-4 bg-ink-soft">
            <span className="font-mono text-[10px] tracking-widest text-rule flex-shrink-0 mt-0.5">
              FILTERS
            </span>
            <span className="font-mono text-xs text-text-lo leading-relaxed">{filters}</span>
          </div>
        )}

        {/* ── Results table ───────────────────────────────────────────── */}
        <div className="mt-4 card-surface overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-haze">
                {['Ticker', 'RVOL', 'Price', 'Change %', 'Volume'].map(col => (
                  <th
                    key={col}
                    className="px-6 py-3 text-left font-mono text-[10px] tracking-[0.15em] text-text-lo"
                  >
                    {col.toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stocks.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-16 text-center">
                    <Activity className="w-8 h-8 mx-auto mb-4 text-text-lo/20" />
                    <div className="font-mono text-xs text-text-lo/30 tracking-widest">
                      NO RESULTS YET
                    </div>
                    <div className="font-mono text-[10px] text-text-lo/20 mt-2 tracking-wider">
                      SELECT A DATE AND TIME, THEN RUN THE SCREENER
                    </div>
                  </td>
                </tr>
              ) : (
                stocks.map((stock, idx) => (
                  <tr
                    key={`${stock.ticker}-${idx}`}
                    className="border-b border-haze/50 hover:bg-haze/30 transition-colors row-fade-in"
                  >
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm font-bold text-text-hi">
                        {stock.ticker}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm font-semibold text-rule">
                        {formatNumber(stock.rvol)}%
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm text-text-hi">
                        ${formatNumber(stock.price)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 font-mono text-sm font-semibold ${
                        stock.change_pct >= 0 ? 'text-signal' : 'text-warn'
                      }`}>
                        {stock.change_pct >= 0
                          ? <TrendingUp className="w-3.5 h-3.5" />
                          : <TrendingDown className="w-3.5 h-3.5" />
                        }
                        {stock.change_pct >= 0 ? '+' : ''}{formatNumber(stock.change_pct)}%
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm text-text-lo">
                        {formatVolume(stock.volume)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </div>

      <style>{`
        @keyframes rowFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .row-fade-in { animation: rowFadeIn 0.2s ease-out; }
      `}</style>

    </div>
  );
};

export default Stage1Dashboard;
